import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Upload, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { TRACKS } from "@/data/playalong";
import { api } from "@/lib/api";
import { loadBacking, VARIANT_LABEL, pauseOthers } from "@/components/StudioBand";

const TrackRow = ({ id, track, rec, prompt, onChange }) => {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const upload = async (variant, file) => {
    if (!file) return;
    setBusy(variant);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("bpm", String(rec?.full?.bpm || track.bpm));
    fd.append("key", rec?.full?.key || track.key);
    try { await api.post(`/admin/backing/${id}/${variant}`, fd); toast.success(`${VARIANT_LABEL[variant]} uploaded for ${track.title}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };
  const remove = async (variant) => {
    setBusy(variant);
    await api.delete(`/admin/backing/${id}/${variant}`).catch(() => {});
    setBusy(""); onChange();
  };
  const regenerate = async () => {
    if (rec?.full && rec.full.source !== "ai" && !window.confirm(`This replaces your own recording of ${track.title} with a new AI version for learners. Continue?`)) return;
    setBusy("regen");
    try {
      await api.post(`/admin/backing-regenerate/${id}`, { prompt, note, bpm: track.bpm, key: track.key }, { timeout: 320000 });
      toast.success(`New AI recording ready for ${track.title}`); setNote(""); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not regenerate"); }
    setBusy("");
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`backing-row-${id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-serif text-lg">{track.title}</p>
        <p className="font-mono text-xs text-muted-foreground">{track.genre} · {track.bpm} bpm · key of {track.key}</p>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {Object.entries(VARIANT_LABEL).map(([v, label]) => {
          const r = rec?.[v];
          return (
            <div key={v} className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm" data-testid={`backing-${id}-${v}`}>
              <span><strong>{label}</strong> <span className="text-xs text-muted-foreground">{r ? `· ${r.label}` : "· none yet"}</span></span>
              <span className="flex items-center gap-2">
                {r && <audio controls preload="none" src={r.url} className="h-8 w-36" onPlay={pauseOthers} data-testid={`backing-audio-${id}-${v}`} />}
                <label className="cursor-pointer text-primary" title="Upload your recording" data-testid={`backing-upload-${id}-${v}`}>
                  {busy === v ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  <input type="file" accept="audio/*" className="hidden" onChange={(e) => upload(v, e.target.files[0])} data-testid={`backing-file-${id}-${v}`} />
                </label>
                {r?.url?.includes("/api/backing/") && <button type="button" onClick={() => remove(v)} title="Remove upload" data-testid={`backing-delete-${id}-${v}`}><Trash2 size={15} className="text-muted-foreground hover:text-destructive" /></button>}
              </span>
            </div>
          );
        })}
      </div>
      {prompt && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder='Style note, e.g. "more Hammond, lighter drums"' data-testid={`backing-note-${id}`}
            className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
          <button type="button" onClick={regenerate} disabled={busy === "regen"} data-testid={`backing-regen-${id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary disabled:opacity-60">
            {busy === "regen" ? <><Loader2 size={15} className="animate-spin" /> Generating (about 1 min)…</> : <><RefreshCw size={15} /> Regenerate AI version</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default function AdminBacking() {
  const { user } = useAuth();
  const [idx, setIdx] = useState({});
  const [prompts, setPrompts] = useState({});
  const refresh = () => loadBacking(true).then(setIdx);
  useEffect(() => {
    refresh();
    fetch("/media/backing/index.json").then((r) => r.json()).then((m) => setPrompts(Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v.prompt])))).catch(() => {});
  }, []);
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-8" data-testid="admin-backing-page">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="admin-backing-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl tracking-tight sm:text-5xl">Backing tracks</h1>
        <p className="mt-3 max-w-2xl text-base text-muted-foreground">Upload your own band recordings (MP3 is best, up to 40MB) as a full band and, if you like, versions without drums, bass or keys. Your uploads replace the AI recordings for learners straight away. Regenerating an AI version uses your ElevenLabs music credit.</p>
        <div className="mt-8 space-y-4">
          {Object.entries(TRACKS).map(([id, t]) => <TrackRow key={id} id={id} track={t} rec={idx[id]} prompt={prompts[id]} onChange={refresh} />)}
        </div>
      </main>
    </div>
  );
}
